import { useState, useEffect, useCallback } from "react";
import {
  Sparkles, Loader2, RefreshCw, TrendingUp, TrendingDown, AlertTriangle,
  ShoppingCart, Wallet, Bike, Utensils, Flame, Target, DollarSign, PiggyBank,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import api from "../../lib/api";
import { PriceChip } from "../common/PriceChip";
import { showCreditReceipt } from "../../lib/creditReceipt";

const ICONS = {
  TrendingUp, TrendingDown, AlertTriangle, Sparkles,
  ShoppingCart, Wallet, Bike, Utensils, Flame, Target, DollarSign, PiggyBank,
};

const SEV_TONES = {
  positive: {
    card: "border-emerald-200 bg-emerald-50/50",
    iconBg: "bg-emerald-100 text-emerald-700",
    title: "text-emerald-900",
  },
  warning: {
    card: "border-amber-200 bg-amber-50/50",
    iconBg: "bg-amber-100 text-amber-800",
    title: "text-amber-900",
  },
  info: {
    card: "border-sky-200 bg-sky-50/50",
    iconBg: "bg-sky-100 text-sky-700",
    title: "text-sky-900",
  },
};

export default function FinanceInsights({ restaurantId, range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (force = false) => {
    if (!restaurantId || !range?.from || !range?.to) return;
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const d = await api.getFinanceInsights(restaurantId, { ...range, refresh: force });
      setData(d);
      if (force) showCreditReceipt(d?._credits_receipt);
    } catch (err) {
      if (force) toast.error("No se pudieron generar los insights");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [restaurantId, range]);

  useEffect(() => { load(false); }, [load]);

  if (loading) {
    return (
      <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50">
        <CardContent className="p-5 flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-violet-600" />
          <div>
            <div className="text-sm font-semibold text-violet-900">Analizando tus números con IA…</div>
            <div className="text-xs text-violet-700/70">Esto tarda unos segundos la primera vez del día</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.insights || data.insights.length === 0) {
    return null;
  }

  return (
    <Card className="border-violet-200 bg-gradient-to-br from-violet-50/60 to-indigo-50/60" data-testid="finance-insights-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" />
            <h3 className="font-semibold text-sm text-foreground">Insights del asesor IA</h3>
            {data.from_cache && data.age_hours != null && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-card/60 border border-violet-200 text-violet-700">
                actualizado hace {data.age_hours < 1 ? "minutos" : `${Math.floor(data.age_hours)}h`}
              </span>
            )}
          </div>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="text-xs text-violet-700 hover:text-violet-900 inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-white/60 transition disabled:opacity-50"
            data-testid="finance-insights-refresh"
          >
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            {refreshing ? "Regenerando…" : "Regenerar"}
            {!refreshing && <PriceChip restaurantId={restaurantId} action="finance_insights_refresh" compact />}
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-2.5">
          {data.insights.map((ins, i) => {
            const Icon = ICONS[ins.icon] || Sparkles;
            const tone = SEV_TONES[ins.severity] || SEV_TONES.info;
            return (
              <div
                key={i}
                className={`rounded-xl border ${tone.card} p-3 flex gap-2.5`}
                data-testid={`finance-insight-${i}`}
              >
                <div className={`shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${tone.iconBg}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${tone.title}`}>{ins.title}</div>
                  <div className="text-xs text-foreground mt-0.5 leading-snug">{ins.body}</div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
